/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/types/RootState";
import LoaderSpinner from "@/components/ReactBootstrap/LoaderSpinner/LoaderSpinner";
import NoDataContainer from "@/cleanup/containers/NoData/NoDataContainer";
import { useDataFilterTable } from "@/components/SntTableViewCard/hooks/useDataFilterTable";
import { DynamicTableStyles } from "./DynamicTableStyles";
import { DynamicTableHeader } from "./DynamicTableHeader";
import { DynamicTableBody } from "./DynamicTableBody";
import { SntTablePagingRef } from "../TableFooter/SntTablePaging";

interface DynamicTableProps {
  dataFilter: any;
  data: any;
  loading?: boolean;
  noDataText?: string;
  isShowSelectPageSize?: boolean;
  isShowPaging?: boolean;
  isShowDecoratorColor?: boolean;
  onRenderConfig?: (original: any) => React.ReactNode;
  height: number;
}

export const DynamicTable = ({
  dataFilter,
  data,
  loading,
  noDataText,
  isShowSelectPageSize = true,
  isShowPaging = true,
  isShowDecoratorColor = false,
  onRenderConfig,
  height,
}: DynamicTableProps) => {
  const defaultColumn = React.useMemo(
    () => ({
      maxWidth: 600,
    }),
    []
  );

  const { isMobile } = useSelector((state: RootState) => state.all);

  const table = useDataFilterTable(dataFilter || ({} as any), data, {
    defaultColumn: defaultColumn,
  });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    totalItems,
    toggleSortBy,
    state: { pageIndex, pageSize },
  } = table;

  const tableProps = getTableProps();
  tableProps.style = tableProps.style || {};
  const existingMinWidth = parseInt(tableProps.style.minWidth || "0", 10);
  tableProps.style.minWidth = existingMinWidth + 40 + "px";

  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);

  // Observe footer height
  useEffect(() => {
    const footerElement = footerRef.current;

    if (footerElement) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setFooterHeight(entry.contentRect.height);
        }
      });

      resizeObserver.observe(footerElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  // Calculate scroll container height (total height minus footer minus padding)
  const scrollContainerHeight = `calc(${height - footerHeight}px - 2rem ${isMobile ? "- 30px" : ""})`;

  return (
    <DynamicTableStyles>
      {/* Main scrolling container - fixed height, contains both header and body */}
      <div
        className="dynamic-table-scroll-container"
        style={{ height: scrollContainerHeight }}
      >
        <div {...tableProps} className="table dynamic mb-0">
          {/* Sticky header - sticks to top of scroll container */}
          <div className="dynamic-table-header">
            <DynamicTableHeader
              headerGroups={headerGroups}
              toggleSortBy={toggleSortBy}
              isGroupedMode={table.isGroupedMode}
              onRenderConfig={onRenderConfig}
            />
          </div>

          {/* Table body */}
          {rows.length === 0 ? (
            <NoDataContainer noDataText={noDataText} loading={loading} />
          ) : (
            <DynamicTableBody
              rows={rows}
              prepareRow={prepareRow}
              getTableBodyProps={getTableBodyProps}
              isShowDecoratorColor={isShowDecoratorColor}
              isGroupedMode={table.isGroupedMode}
              onRenderConfig={onRenderConfig}
              loading={loading}
            />
          )}
        </div>
      </div>

      {/* Footer - outside scroll container, fixed at bottom */}
      {isShowPaging && (
        <div className="dynamic-table-footer">
          <SntTablePagingRef
            ref={footerRef}
            gotoPage={gotoPage}
            previousPage={previousPage}
            nextPage={nextPage}
            pageCount={pageCount}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            pageSize={pageSize}
            setPageSize={setPageSize}
            isShowSelectPageSize={isShowSelectPageSize}
            totalItems={totalItems}
            headerGroups={headerGroups}
          />
        </div>
      )}

      <LoaderSpinner loading={loading} />
    </DynamicTableStyles>
  );
};
